/**
 * Saved-report builder (Part 7, A7.3).
 *
 * Lets users define a query (entity + filters + group-by + columns),
 * preview rows, save it, and export to CSV / XLSX / PDF / PPTX / JSON.
 * Existing ScheduledReportsManager can then schedule a saved report.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Download, Play, Save, Loader2 } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import {
  savedReportsService,
  type SavedReport,
  type ReportMeta,
  type ReportEntity,
  type FilterOp,
  type FilterClause,
  type ReportFormat,
} from '../services/savedReports'
import type { ApiErrorLike } from '../utils/apiError'

const EXPORT_FORMATS: ReportFormat[] = ['csv', 'xlsx', 'pdf', 'pptx', 'json']

export function ReportBuilder() {
  const [meta, setMeta] = useState<ReportMeta | null>(null)
  const [reports, setReports] = useState<SavedReport[]>([])
  const [active, setActive] = useState<SavedReport | null>(null)
  const [draft, setDraft] = useState({
    name: '', description: '', entity_type: 'project' as ReportEntity,
    filters: [] as FilterClause[], group_by: [] as string[], columns: [] as string[],
  })
  const [preview, setPreview] = useState<{ columns: string[]; rows: Record<string, unknown>[]; row_count: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => { (async () => {
    setLoading(true)
    try {
      const [m, list] = await Promise.all([savedReportsService.meta(), savedReportsService.list()])
      setMeta(m); setReports(list)
    } finally { setLoading(false) }
  })() }, [])

  const allFields = useMemo(() => meta?.entities[draft.entity_type] || [], [meta, draft.entity_type])

  function reset() {
    setActive(null)
    setDraft({ name: '', description: '', entity_type: 'project', filters: [], group_by: [], columns: [] })
    setPreview(null)
  }

  function loadReport(r: SavedReport) {
    setActive(r)
    setDraft({
      name: r.name, description: r.description || '',
      entity_type: r.entity_type, filters: [...r.filters],
      group_by: [...r.group_by], columns: [...r.columns],
    })
    setPreview(null)
  }

  async function save() {
    if (!draft.name.trim()) { toast.error('Name is required'); return }
    setBusy(true)
    try {
      const saved = active
        ? await savedReportsService.update(active.id, {
            name: draft.name, description: draft.description,
            filters: draft.filters, group_by: draft.group_by, columns: draft.columns,
          })
        : await savedReportsService.create({
            name: draft.name, description: draft.description,
            entity_type: draft.entity_type,
            filters: draft.filters, group_by: draft.group_by, columns: draft.columns,
          })
      setReports(prev => active ? prev.map(r => r.id === saved.id ? saved : r) : [saved, ...prev])
      setActive(saved)
      toast.success('Saved')
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Save failed')
    } finally { setBusy(false) }
  }

  async function run() {
    if (!active) { toast.error('Save the report first'); return }
    setBusy(true)
    try { setPreview(await savedReportsService.run(active.id)) }
    catch { toast.error('Run failed') }
    finally { setBusy(false) }
  }

  async function exportAs(fmt: ReportFormat) {
    if (!active) { toast.error('Save the report first'); return }
    setBusy(true)
    try { await savedReportsService.download(active.id, fmt, active.name) }
    catch { toast.error('Export failed') }
    finally { setBusy(false) }
  }

  async function deleteReport() {
    if (!active) return
    if (!confirm(`Delete "${active.name}"?`)) return
    try {
      await savedReportsService.remove(active.id)
      setReports(prev => prev.filter(r => r.id !== active.id))
      reset()
    } catch { toast.error('Delete failed') }
  }

  function addFilter() {
    setDraft(d => ({ ...d, filters: [...d.filters, { field: allFields[0] || '', op: 'eq', value: '' }] }))
  }

  function updateFilter(idx: number, patch: Partial<FilterClause>) {
    setDraft(d => ({ ...d, filters: d.filters.map((f, i) => i === idx ? { ...f, ...patch } : f) }))
  }

  function removeFilter(idx: number) {
    setDraft(d => ({ ...d, filters: d.filters.filter((_, i) => i !== idx) }))
  }

  function toggleColumn(field: string) {
    setDraft(d => ({
      ...d,
      columns: d.columns.includes(field) ? d.columns.filter(c => c !== field) : [...d.columns, field],
    }))
  }

  function toggleGroup(field: string) {
    setDraft(d => ({
      ...d,
      group_by: d.group_by.includes(field) ? d.group_by.filter(c => c !== field) : [...d.group_by, field],
    }))
  }

  if (loading || !meta) return <div className="flex justify-center py-12"><Loader2 className="size-5 animate-spin" /></div>

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      <aside className="rounded-lg border bg-card p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Saved Reports</h3>
          <Button size="sm" variant="outline" onClick={reset} className="min-h-[36px]"><Plus className="size-3.5" /></Button>
        </div>
        {reports.length === 0 ? (
          <p className="text-xs text-muted-foreground">No saved reports yet.</p>
        ) : (
          <ul className="space-y-1">
            {reports.map(r => (
              <li key={r.id}>
                <button onClick={() => loadReport(r)}
                  className={`w-full text-left rounded-md px-2 py-1.5 text-sm hover:bg-accent ${active?.id === r.id ? 'bg-accent' : ''}`}>
                  <div className="truncate font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{r.entity_type.replace('_', ' ')}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="space-y-4">
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rb-name">Name</Label>
              <Input id="rb-name" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="rb-entity">Entity</Label>
              <Select value={draft.entity_type}
                onValueChange={v => setDraft(d => ({ ...d, entity_type: v as ReportEntity, filters: [], group_by: [], columns: [] }))}
                disabled={!!active}>
                <SelectTrigger id="rb-entity" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(meta.entities).map(k => (
                    <SelectItem key={k} value={k} className="capitalize">{k.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="rb-desc">Description</Label>
            <Input id="rb-desc" value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} className="mt-1" />
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Filters</h3>
            <Button size="sm" variant="outline" onClick={addFilter}><Plus className="size-3.5 mr-1" /> Add filter</Button>
          </div>
          {draft.filters.length === 0 ? (
            <p className="text-xs text-muted-foreground">No filters — all rows included.</p>
          ) : (
            <ul className="space-y-2">
              {draft.filters.map((f, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <Select value={f.field} onValueChange={v => updateFilter(i, { field: v })}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>{allFields.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={f.op} onValueChange={v => updateFilter(i, { op: v as FilterOp })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.ops.map(op => <SelectItem key={op} value={op}>{op}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="flex-1 min-w-[160px]" value={String(f.value ?? '')}
                    onChange={e => updateFilter(i, { value: e.target.value })} placeholder="value" />
                  <Button size="icon" variant="ghost" onClick={() => removeFilter(i)}
                    className="text-destructive hover:text-destructive"><Trash2 className="size-4" /></Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-2">Columns</h3>
            <div className="flex flex-wrap gap-1">
              {allFields.map(f => (
                <Badge key={f} variant={draft.columns.includes(f) ? 'default' : 'outline'}
                  className="cursor-pointer" onClick={() => toggleColumn(f)}>{f}</Badge>
              ))}
            </div>
            {draft.columns.length === 0 && <p className="text-xs text-muted-foreground mt-2">No columns selected — all available will export.</p>}
          </div>
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-2">Group by</h3>
            <div className="flex flex-wrap gap-1">
              {allFields.map(f => (
                <Badge key={f} variant={draft.group_by.includes(f) ? 'default' : 'outline'}
                  className="cursor-pointer" onClick={() => toggleGroup(f)}>{f}</Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={busy} className="min-h-[44px]">
            {busy ? <Loader2 className="size-4 animate-spin mr-1" /> : <Save className="size-4 mr-1" />}
            {active ? 'Update' : 'Save'}
          </Button>
          <Button onClick={run} disabled={busy || !active} variant="outline" className="min-h-[44px]">
            <Play className="size-4 mr-1" /> Preview
          </Button>
          {EXPORT_FORMATS.map(f => (
            <Button key={f} onClick={() => exportAs(f)} disabled={busy || !active}
              variant="outline" size="sm" className="min-h-[44px]">
              <Download className="size-3.5 mr-1" /> {f.toUpperCase()}
            </Button>
          ))}
          {active && (
            <Button onClick={deleteReport} variant="ghost" size="sm"
              className="ml-auto text-destructive hover:text-destructive">
              <Trash2 className="size-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>

        {preview && (
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Preview</h3>
              <span className="text-xs text-muted-foreground">{preview.row_count} rows</span>
            </div>
            <div className="overflow-auto max-h-[480px]">
              <Table>
                <TableHeader>
                  <TableRow>{preview.columns.map(c => <TableHead key={c}>{c}</TableHead>)}</TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 200).map((r, i) => (
                    <TableRow key={i}>
                      {preview.columns.map(c => <TableCell key={c} className="text-xs">{String(r[c] ?? '')}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
